/**
 * AI Design Service
 *
 * Bridges AI command responses to FabricComponent canvas operations.
 * Each command maps to a Fabric.js operation on the canvas.
 */
import fabric from '@/components/_fabric'
import convert from 'color-convert'

/**
 * Convert hex color to CMYK object
 */
function hexToCmyk(hex) {
  hex = hex.replace('#', '')
  const r = parseInt(hex.substring(0, 2), 16)
  const g = parseInt(hex.substring(2, 4), 16)
  const b = parseInt(hex.substring(4, 6), 16)
  const cmyk = convert.rgb.cmyk([r, g, b])
  return {
    cyan: cmyk[0],
    magenta: cmyk[1],
    yellow: cmyk[2],
    black: cmyk[3]
  }
}

/**
 * Extract dominant colors from an image (base64 data URL).
 * Uses canvas pixel sampling to find the top N most frequent colors.
 *
 * @param {string} imageDataUrl - base64 data URL of the image
 * @param {number} numColors - number of dominant colors to extract
 * @returns {Promise<string[]>} - array of hex color strings
 */
/**
 * Check if a color is near-white (background candidate)
 */
function isNearWhite(r, g, b) {
  return r > 220 && g > 220 && b > 220
}

/**
 * Check if a color is near-black (background candidate)
 */
function isNearBlack(r, g, b) {
  return r < 35 && g < 35 && b < 35
}

/**
 * Check if a color is a neutral gray (low saturation)
 */
function isNeutralGray(r, g, b) {
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const diff = max - min
  // Low chroma and in the mid-range
  return diff < 30 && max > 50 && max < 210
}

/**
 * Extract and classify brand colors from an image (base64 data URL).
 * Filters out background colors (white, black, gray) and identifies
 * primary and secondary brand colors.
 *
 * @param {string} imageDataUrl - base64 data URL of the image
 * @param {number} numColors - number of total colors to extract
 * @returns {Promise<{primary: string|null, secondary: string|null, background: string|null, all: string[]}>}
 */
export function extractColors(imageDataUrl, numColors = 6) {
  return new Promise((resolve) => {
    const img = new Image()
    img.crossOrigin = 'Anonymous'
    img.onload = () => {
      const canvas = document.createElement('canvas')
      // Sample at a small size for performance
      const sampleSize = 80
      canvas.width = sampleSize
      canvas.height = sampleSize
      const ctx = canvas.getContext('2d')
      // Fill white first to handle transparency
      ctx.fillStyle = '#FFFFFF'
      ctx.fillRect(0, 0, sampleSize, sampleSize)
      ctx.drawImage(img, 0, 0, sampleSize, sampleSize)

      const imageData = ctx.getImageData(0, 0, sampleSize, sampleSize)
      const pixels = imageData.data
      const totalPixels = sampleSize * sampleSize
      const colorCounts = {}
      const rawColors = {} // store original RGB for each quantized color

      for (let i = 0; i < pixels.length; i += 4) {
        const r = pixels[i]
        const g = pixels[i + 1]
        const b = pixels[i + 2]

        // Quantize to reduce color space (group similar colors)
        const qr = Math.round(r / 24) * 24
        const qg = Math.round(g / 24) * 24
        const qb = Math.round(b / 24) * 24

        const hex = '#' +
          Math.min(qr, 255).toString(16).padStart(2, '0') +
          Math.min(qg, 255).toString(16).padStart(2, '0') +
          Math.min(qb, 255).toString(16).padStart(2, '0')

        colorCounts[hex] = (colorCounts[hex] || 0) + 1
        if (!rawColors[hex]) rawColors[hex] = { r: qr, g: qg, b: qb }
      }

      // Sort all colors by frequency
      const sorted = Object.entries(colorCounts)
        .sort((a, b) => b[1] - a[1])

      // Separate brand colors from background colors
      const brandColors = []
      let backgroundColorHex = null
      let backgroundCount = 0

      sorted.forEach(([hex, count]) => {
        const { r, g, b } = rawColors[hex]
        const isBackground = isNearWhite(r, g, b) || isNearBlack(r, g, b) || isNeutralGray(r, g, b)
        const percentage = count / totalPixels

        if (isBackground) {
          // Track the most dominant background color
          if (count > backgroundCount) {
            backgroundColorHex = hex
            backgroundCount = count
          }
        } else {
          brandColors.push({ hex, count, percentage })
        }
      })

      // Build the result
      const allBrandHexes = brandColors.slice(0, numColors).map(c => c.hex)
      const primary = brandColors.length > 0 ? brandColors[0].hex : null
      const secondary = brandColors.length > 1 ? brandColors[1].hex : null

      resolve({
        primary,
        secondary,
        background: backgroundColorHex,
        all: allBrandHexes,
      })
    }
    img.onerror = () => resolve({ primary: null, secondary: null, background: null, all: [] })
    img.src = imageDataUrl
  })
}

/**
 * Execute a list of AI commands on the FabricComponent
 *
 * @param {Array} commands - Array of command objects from the AI
 * @param {Object} fabricComponent - Reference to the FabricComponent Vue instance
 */
export function executeAiCommands(commands, fabricComponent) {
  if (!commands || !Array.isArray(commands) || !fabricComponent) return

  const canvas = fabricComponent.canvas
  if (!canvas) return

  commands.forEach(cmd => {
    try {
      switch (cmd.action) {
        case 'clearCanvas':
          executeClearCanvas(fabricComponent)
          break
        case 'setBackgroundColor':
          executeSetBackgroundColor(cmd.params, fabricComponent)
          break
        case 'setBackgroundImage':
          executeSetBackgroundImage(cmd.params, canvas, fabricComponent)
          break
        case 'addText':
          executeAddText(cmd.params, canvas, fabricComponent)
          break
        case 'addCircle':
          executeAddCircle(cmd.params, canvas)
          break
        case 'addRectangle':
          executeAddRectangle(cmd.params, canvas)
          break
        case 'addImage':
          executeAddImage(cmd.params, canvas)
          break
        case 'addTriangle':
          executeAddTriangle(cmd.params, canvas)
          break
        case 'addLine':
          executeAddLine(cmd.params, canvas)
          break
        case 'addEllipse':
          executeAddEllipse(cmd.params, canvas)
          break
        case 'modifyObject':
          executeModifyObject(cmd.params, canvas)
          break
        case 'removeObject':
          executeRemoveObject(cmd.params, canvas)
          break
        case 'addPolygon':
          executeAddPolygon(cmd.params, canvas)
          break
        case 'addGradientBackground':
          executeAddGradientBackground(cmd.params, fabricComponent)
          break
        case 'setObjectShadow':
          executeSetObjectShadow(cmd.params, canvas)
          break
        case 'setObjectOpacity':
          executeSetObjectOpacity(cmd.params, canvas)
          break
        case 'addTextbox':
          executeAddTextbox(cmd.params, canvas, fabricComponent)
          break
        default:
          console.warn(`AI command not recognized: ${cmd.action}`)
      }
    } catch (e) {
      console.error(`Error executing AI command ${cmd.action}:`, e)
    }
  })

  // Final render and history save
  canvas.renderAll()
  canvas._historySaveAction()
}

// ─── Shared styling for interactive objects ───
const INTERACTIVE_STYLE = {
  borderColor: '#23B6BB',
  borderScaleFactor: 3,
  borderOpacityWhenMoving: 1,
  cornerColor: '#D9C731',
  cornerStrokeColor: '#D9C731',
  transparentCorners: false,
  cornerStyle: 'circle',
  cornerSize: 20,
}

function executeClearCanvas(fabricComponent) {
  fabricComponent.canvas.clear()
  fabricComponent.croppedBg = ''
  fabricComponent.canvas.backgroundColor = fabricComponent.canvasInitColor
  fabricComponent.canvas.renderAll()
  fabricComponent.addTurtleLogo()
}

function executeSetBackgroundColor(params, fabricComponent) {
  const color = params.color || '#FFFFFF'
  const cmyk = hexToCmyk(color)
  fabricComponent.backgroundColorCMYK.cyan = cmyk.cyan
  fabricComponent.backgroundColorCMYK.magenta = cmyk.magenta
  fabricComponent.backgroundColorCMYK.yellow = cmyk.yellow
  fabricComponent.backgroundColorCMYK.black = cmyk.black
  fabricComponent.canvas.backgroundColor = color
  fabricComponent.canvas.renderAll()
}

function executeSetBackgroundImage(params, canvas, fabricComponent) {
  const imageData = params.imageData
  if (!imageData) {
    console.warn('setBackgroundImage command received but no imageData provided')
    return
  }

  new fabric.Image.fromURL(imageData, (img) => {
    canvas.setBackgroundImage(img, canvas.renderAll.bind(canvas), {
      scaleX: canvas.width / img.width,
      scaleY: canvas.height / img.height
    })
    canvas.renderAll()
    canvas._historySaveAction()
  })
}

function executeAddText(params, canvas, fabricComponent) {
  const text = params.text || 'Text'
  const fontSize = params.fontSize || 60
  const fontFamily = params.fontFamily || 'ubuntu'
  const color = params.color || '#000000'
  const x = params.x != null ? params.x : fabricComponent.canvasWidth / 2
  const y = params.y != null ? params.y : fabricComponent.canvasHeight / 2

  let FontFaceObserver = require('fontfaceobserver')
  let myfont = new FontFaceObserver(fontFamily)
  myfont.load()
    .then(function () {
      const textObj = new fabric.IText(text, {
        width: 400,
        textAlign: 'center',
        originX: 'center',
        originY: 'center',
        fontSize: fontSize,
        fill: color,
        fontFamily: fontFamily,
        top: y,
        left: x,
        ...INTERACTIVE_STYLE,
      })
      canvas.add(textObj)

      let turtleObj = findTurtle(canvas)
      if (turtleObj) turtleObj.bringToFront()

      canvas.renderAll()
      canvas._historySaveAction()
    })
    .catch(function () {
      const textObj = new fabric.IText(text, {
        width: 400,
        textAlign: 'center',
        originX: 'center',
        originY: 'center',
        fontSize: fontSize,
        fill: color,
        fontFamily: fontFamily,
        top: y,
        left: x,
        ...INTERACTIVE_STYLE,
      })
      canvas.add(textObj)
      canvas.renderAll()
      canvas._historySaveAction()
    })
}

function executeAddCircle(params, canvas) {
  const radius = params.radius || 50
  const color = params.color || '#000000'
  const fill = params.fill || 'transparent'
  const strokeWidth = params.strokeWidth || 7
  const x = params.x || 100
  const y = params.y || 100

  const circle = new fabric.Circle({
    radius: radius,
    fill: fill,
    left: x,
    top: y,
    hasBorders: true,
    hasControls: true,
    stroke: color,
    strokeWidth: strokeWidth,
    ...INTERACTIVE_STYLE,
  })

  canvas.add(circle)
  let turtleObj = findTurtle(canvas)
  if (turtleObj) turtleObj.bringToFront()
}

function executeAddRectangle(params, canvas) {
  const width = params.width || 100
  const height = params.height || 100
  const color = params.color || '#000000'
  const fill = params.fill || 'transparent'
  const strokeWidth = params.strokeWidth || 7
  const x = params.x || 100
  const y = params.y || 100

  const rect = new fabric.Rect({
    width: width,
    height: height,
    left: x,
    top: y,
    stroke: color,
    strokeWidth: strokeWidth,
    fill: fill,
    ...INTERACTIVE_STYLE,
  })

  canvas.add(rect)
  let turtleObj = findTurtle(canvas)
  if (turtleObj) turtleObj.bringToFront()
}

function executeAddImage(params, canvas) {
  const imageData = params.imageData
  if (!imageData) {
    console.warn('addImage command received but no imageData provided')
    return
  }

  const x = params.x || 500
  const y = params.y || 200
  const maxWidth = params.maxWidth || 300
  const maxHeight = params.maxHeight || 200

  new fabric.Image.fromURL(imageData, (img) => {
    const scale = Math.min(maxWidth / img.width, maxHeight / img.height, 1)
    img.set({
      left: x,
      top: y,
      originX: 'center',
      originY: 'center',
      scaleX: scale,
      scaleY: scale,
    })

    canvas.add(img)

    let turtleObj = findTurtle(canvas)
    if (turtleObj) turtleObj.bringToFront()

    canvas.renderAll()
    canvas._historySaveAction()
  }, INTERACTIVE_STYLE)
}

// ─── New commands (8-17) ───

function executeAddTriangle(params, canvas) {
  const width = params.width || 100
  const height = params.height || 100
  const color = params.color || '#000000'
  const fill = params.fill || 'transparent'
  const strokeWidth = params.strokeWidth || 7
  const x = params.x || 100
  const y = params.y || 100
  const angle = params.angle || 0

  const triangle = new fabric.Triangle({
    width: width,
    height: height,
    left: x,
    top: y,
    stroke: color,
    strokeWidth: strokeWidth,
    fill: fill,
    angle: angle,
    ...INTERACTIVE_STYLE,
  })

  canvas.add(triangle)
  let turtleObj = findTurtle(canvas)
  if (turtleObj) turtleObj.bringToFront()
}

function executeAddLine(params, canvas) {
  const x1 = params.x1 || 0
  const y1 = params.y1 || 0
  const x2 = params.x2 || 200
  const y2 = params.y2 || 0
  const color = params.color || '#000000'
  const strokeWidth = params.strokeWidth || 3
  const strokeDashArray = params.strokeDashArray || null

  const lineOpts = {
    stroke: color,
    strokeWidth: strokeWidth,
    ...INTERACTIVE_STYLE,
  }
  if (strokeDashArray) {
    lineOpts.strokeDashArray = strokeDashArray
  }

  const line = new fabric.Line([x1, y1, x2, y2], lineOpts)

  canvas.add(line)
  let turtleObj = findTurtle(canvas)
  if (turtleObj) turtleObj.bringToFront()
}

function executeAddEllipse(params, canvas) {
  const rx = params.rx || 80
  const ry = params.ry || 50
  const color = params.color || '#000000'
  const fill = params.fill || 'transparent'
  const strokeWidth = params.strokeWidth || 7
  const x = params.x || 100
  const y = params.y || 100

  const ellipse = new fabric.Ellipse({
    rx: rx,
    ry: ry,
    left: x,
    top: y,
    stroke: color,
    strokeWidth: strokeWidth,
    fill: fill,
    ...INTERACTIVE_STYLE,
  })

  canvas.add(ellipse)
  let turtleObj = findTurtle(canvas)
  if (turtleObj) turtleObj.bringToFront()
}

function executeModifyObject(params, canvas) {
  const objectIndex = params.objectIndex
  if (objectIndex == null) {
    console.warn('modifyObject: objectIndex is required')
    return
  }

  // Get non-turtle objects for indexing
  const objects = canvas.getObjects().filter(obj => obj.id !== 'turtle')
  if (objectIndex < 0 || objectIndex >= objects.length) {
    console.warn(`modifyObject: objectIndex ${objectIndex} out of range (${objects.length} objects)`)
    return
  }

  const obj = objects[objectIndex]
  const props = params.properties || {}

  // Map supported properties
  const allowedProps = [
    'left', 'top', 'fill', 'stroke', 'strokeWidth', 'opacity',
    'angle', 'scaleX', 'scaleY', 'fontSize', 'fontFamily',
    'text', 'width', 'height', 'radius', 'rx', 'ry',
    'flipX', 'flipY'
  ]

  const updates = {}
  allowedProps.forEach(prop => {
    if (props[prop] !== undefined) {
      updates[prop] = props[prop]
    }
  })

  if (Object.keys(updates).length > 0) {
    obj.set(updates)
    obj.setCoords()
    canvas.renderAll()
  }
}

function executeRemoveObject(params, canvas) {
  const objectIndex = params.objectIndex
  if (objectIndex == null) {
    console.warn('removeObject: objectIndex is required')
    return
  }

  // Get non-turtle objects for indexing
  const objects = canvas.getObjects().filter(obj => obj.id !== 'turtle')
  if (objectIndex < 0 || objectIndex >= objects.length) {
    console.warn(`removeObject: objectIndex ${objectIndex} out of range (${objects.length} objects)`)
    return
  }

  canvas.remove(objects[objectIndex])
  canvas.renderAll()
}

function executeAddPolygon(params, canvas) {
  const points = params.points || [
    { x: 50, y: 0 }, { x: 100, y: 38 }, { x: 82, y: 100 },
    { x: 18, y: 100 }, { x: 0, y: 38 }
  ]
  const color = params.color || '#000000'
  const fill = params.fill || 'transparent'
  const strokeWidth = params.strokeWidth || 7
  const x = params.x || 100
  const y = params.y || 100

  const polygon = new fabric.Polygon(points, {
    left: x,
    top: y,
    stroke: color,
    strokeWidth: strokeWidth,
    fill: fill,
    ...INTERACTIVE_STYLE,
  })

  canvas.add(polygon)
  let turtleObj = findTurtle(canvas)
  if (turtleObj) turtleObj.bringToFront()
}

function executeAddGradientBackground(params, fabricComponent) {
  const canvas = fabricComponent.canvas
  const type = params.type || 'linear'
  const colorStops = params.colorStops || [
    { offset: 0, color: '#000000' },
    { offset: 1, color: '#FFFFFF' }
  ]

  // Default coords: left-to-right for linear, center for radial
  let coords
  if (type === 'radial') {
    coords = params.coords || {
      x1: canvas.width / 2, y1: canvas.height / 2, r1: 0,
      x2: canvas.width / 2, y2: canvas.height / 2, r2: canvas.width / 2
    }
  } else {
    coords = params.coords || {
      x1: 0, y1: 0,
      x2: canvas.width, y2: 0
    }
  }

  // Build color stops object for Fabric
  const stops = {}
  colorStops.forEach(stop => {
    stops[stop.offset] = stop.color
  })

  // Create a full-canvas rect with gradient as the background
  const bgRect = new fabric.Rect({
    width: canvas.width,
    height: canvas.height,
    left: 0,
    top: 0,
    selectable: false,
    evented: false,
    excludeFromExport: false,
  })

  bgRect.setGradient('fill', {
    type: type,
    coords: coords,
    colorStops: stops,
  })

  // Remove existing gradient bg rects if any
  const existingBg = canvas.getObjects().filter(obj => obj._isGradientBg)
  existingBg.forEach(obj => canvas.remove(obj))

  bgRect._isGradientBg = true
  canvas.add(bgRect)
  canvas.sendToBack(bgRect)

  // Clear solid background color
  canvas.backgroundColor = ''
  fabricComponent.backgroundColorCMYK.cyan = 0
  fabricComponent.backgroundColorCMYK.magenta = 0
  fabricComponent.backgroundColorCMYK.yellow = 0
  fabricComponent.backgroundColorCMYK.black = 0

  canvas.renderAll()
}

function executeSetObjectShadow(params, canvas) {
  const objectIndex = params.objectIndex
  if (objectIndex == null) {
    console.warn('setObjectShadow: objectIndex is required')
    return
  }

  const objects = canvas.getObjects().filter(obj => obj.id !== 'turtle')
  if (objectIndex < 0 || objectIndex >= objects.length) {
    console.warn(`setObjectShadow: objectIndex ${objectIndex} out of range`)
    return
  }

  const obj = objects[objectIndex]
  const shadowColor = params.color || 'rgba(0,0,0,0.5)'
  const blur = params.blur || 10
  const offsetX = params.offsetX || 5
  const offsetY = params.offsetY || 5

  obj.set('shadow', new fabric.Shadow({
    color: shadowColor,
    blur: blur,
    offsetX: offsetX,
    offsetY: offsetY,
  }))

  canvas.renderAll()
}

function executeSetObjectOpacity(params, canvas) {
  const objectIndex = params.objectIndex
  if (objectIndex == null) {
    console.warn('setObjectOpacity: objectIndex is required')
    return
  }

  const objects = canvas.getObjects().filter(obj => obj.id !== 'turtle')
  if (objectIndex < 0 || objectIndex >= objects.length) {
    console.warn(`setObjectOpacity: objectIndex ${objectIndex} out of range`)
    return
  }

  const opacity = params.opacity != null ? params.opacity : 1
  objects[objectIndex].set('opacity', Math.max(0, Math.min(1, opacity)))
  canvas.renderAll()
}

function executeAddTextbox(params, canvas, fabricComponent) {
  const text = params.text || 'Text'
  const fontSize = params.fontSize || 40
  const fontFamily = params.fontFamily || 'ubuntu'
  const color = params.color || '#000000'
  const textWidth = params.width || 300
  const x = params.x != null ? params.x : fabricComponent.canvasWidth / 2
  const y = params.y != null ? params.y : fabricComponent.canvasHeight / 2
  const textAlign = params.textAlign || 'center'

  let FontFaceObserver = require('fontfaceobserver')
  let myfont = new FontFaceObserver(fontFamily)

  const createTextbox = () => {
    const textbox = new fabric.Textbox(text, {
      width: textWidth,
      textAlign: textAlign,
      originX: 'center',
      originY: 'center',
      fontSize: fontSize,
      fill: color,
      fontFamily: fontFamily,
      top: y,
      left: x,
      ...INTERACTIVE_STYLE,
    })
    canvas.add(textbox)

    let turtleObj = findTurtle(canvas)
    if (turtleObj) turtleObj.bringToFront()

    canvas.renderAll()
    canvas._historySaveAction()
  }

  myfont.load().then(createTextbox).catch(createTextbox)
}

function findTurtle(canvas) {
  const objects = canvas.getObjects()
  return objects.find(obj => obj.id === 'turtle') || null
}

export default { executeAiCommands, extractColors }
